<script setup lang="ts">
    import { computed, reactive, ref } from 'vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { formatDateTime } from '@shared/utils/formatDateTime';
    import { QuestionType, type Question } from '@views/Surveys/SurveysTypes.d';
    import QuestionMetaBadges from '@views/Surveys/SurveyStatistics/components/QuestionMetaBadges.vue';
    import QuestionAnswer from '@views/Surveys/SurveyStatistics/components/QuestionAnswer.vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormTextArea from '@shared/components/Form/FormTextArea.vue';
    import FormRadioGroup from '@shared/components/Form/FormRadioGroup.vue';
    import FormCheckboxGroup from '@shared/components/Form/FormCheckboxGroup.vue';
    import { createSurveyAnswer, updateSurveyAnswer } from './EventGuestList.access';
    import {
        type EventParticipantAnswer,
        type ParticipantHistoryEntry,
        type ResponseContact,
        type ResponseQuestion,
        type ResponseQuestionOption,
        type ResponseSurveyAnswer,
    } from './guest-response-types';

    // Port of the Knockout survey-question component (Views/Templates/Surveys/survey-question.cshtml),
    // rendered like the survey statistics response detail. Visibility is decided by GuestResponseModal;
    // accompanying guests see only their own entry in the accompanying-guests (EventParticipant) answer.

    const props = defineProps<{
        question: ResponseQuestion;
        answer: ResponseSurveyAnswer | null;
        label: string;
        history: ParticipantHistoryEntry[];
        contact: ResponseContact;
        surveyResponseId: string;
        eventParticipantId: string;
        isContactRow: boolean;
        participantSqiid: string | number | undefined;
        eventEnded: boolean;
        resourcePreviewUrls: Record<string, string>;
    }>();

    const emit = defineEmits<{
        saved: [];
        showContact: [];
    }>();

    const NOT_EDITABLE_TYPES = [
        QuestionType.Content,
        QuestionType.Pagebreak,
        QuestionType.Divider,
        QuestionType.Matrix,
        QuestionType.Attestation,
        QuestionType.FileUpload,
    ];

    const type = computed(() => props.question.Type);
    const isEventParticipant = computed(() => type.value === QuestionType.EventParticipant);

    const editableType = computed(() => !NOT_EDITABLE_TYPES.includes(type.value));
    const allowEditAnswer = computed(() => editableType.value && !props.question.DefaultValue && !props.eventEnded);

    const resources = computed(() => props.answer?.SurveyAnswerResources ?? []);
    const answered = computed(() => (type.value === QuestionType.FileUpload ? resources.value.length > 0 : !!props.answer));

    // The response endpoint returns a subset of the survey Question contract; the shared statistics
    // components only read fields present in it (missing optional flags are treated as false).
    const surveyQuestion = computed(() => props.question as unknown as Question);

    const parseEventParticipantAnswer = (raw: string | null): EventParticipantAnswer => {
        try {
            return (raw ? JSON.parse(raw) : null) ?? {};
        } catch {
            return {};
        }
    };

    const sameSqiid = (sqiid: string | number) => String(sqiid) === String(props.participantSqiid);

    const eventParticipantAnswer = computed(() => (isEventParticipant.value ? parseEventParticipantAnswer(props.answer?.Freetext ?? null) : {}));
    const ownParticipantEntries = computed(() => (eventParticipantAnswer.value.Participants ?? []).filter((p) => sameSqiid(p.Sqiid)));

    const answerOptions = computed(() =>
        (props.answer?.Question.QuestionOptions ?? []).map((o) => ({ QuestionOptionId: o.QuestionOptionId, NumericResponseValue: o.NumericValue }))
    );

    // Shown next to answers prefilled from a contact field ([@Field]) when the contact's current value differs.
    const contactValueDiff = computed(() => {
        const defaultValue = props.question.DefaultValue;
        if (!defaultValue || !props.answer) return null;

        const key = defaultValue.replace('[@', '').replace(']', '');
        const contactValue =
            key === 'Name' ? `${props.contact.FirstName} ${props.contact.LastName}` : ((props.contact[key] as string | null | undefined) ?? '');
        const options = props.answer.Question.QuestionOptions;
        const answerValue = options.length > 0 ? options[0].Value : props.answer.Freetext;

        return contactValue && contactValue !== answerValue ? contactValue : null;
    });

    const latestHistory = computed(() => {
        if (!props.answer) return null;
        const questionId = props.answer.Question.QuestionId;
        const entries = props.history.filter((h) => h.HistoryData?.OldSurveyAnswer?.Question.QuestionId === questionId);
        return entries.sort((a, b) => new Date(b.ModifiedAt).valueOf() - new Date(a.ModifiedAt).valueOf())[0] ?? null;
    });

    const showHistory = computed(() => (props.isContactRow ? !isEventParticipant.value : isEventParticipant.value) && !!latestHistory.value);

    const previousAnswer = computed(() => {
        const old = latestHistory.value?.HistoryData?.OldSurveyAnswer;
        if (!old) return null;

        let freetext = '';
        if (old.Freetext != null) {
            freetext = isEventParticipant.value
                ? (parseEventParticipantAnswer(old.Freetext).Participants ?? [])
                      .map((p) => (p.Fields ?? []).map((f) => `${f.Title}: ${f.Value}`).join(', '))
                      .join(' ')
                : old.Freetext;
        }

        return [freetext, old.Question.QuestionOptions.map((o) => o.Value).join(', ')].filter(Boolean).join(' ');
    });

    // --- Editing ---

    const editing = ref(false);
    const saving = ref(false);
    const edit = reactive<{
        freetext: string;
        participants: EventParticipantAnswer;
        radioOptionId: string;
        // Checkbox state, or the number-input value (number, or "" when cleared) for MultipleNumeric.
        optionValues: Record<string, boolean | number | string | null>;
    }>({ freetext: '', participants: {}, radioOptionId: '', optionValues: {} });

    const optionItems = computed(() => props.question.QuestionOptions.map((o) => ({ label: o.Value ?? '', value: o.QuestionOptionId })));

    function startEdit() {
        const answerOptions = props.answer?.Question.QuestionOptions ?? [];

        edit.freetext = props.answer?.Freetext ?? '';
        // Deep copy so cancelling leaves the displayed answer untouched.
        edit.participants = parseEventParticipantAnswer(props.answer?.Freetext ?? null);
        edit.radioOptionId = '';
        edit.optionValues = {};

        for (const option of props.question.QuestionOptions) {
            const answered = answerOptions.find((o) => o.QuestionOptionId === option.QuestionOptionId);
            if (answered) edit.radioOptionId = option.QuestionOptionId;

            edit.optionValues[option.QuestionOptionId] = type.value === QuestionType.MultipleNumeric ? (answered?.NumericValue ?? null) : !!answered;
        }

        editing.value = true;
    }

    function selectedOptions(): ResponseQuestionOption[] {
        const options = props.question.QuestionOptions;

        if (type.value === QuestionType.Single) {
            return options.filter((o) => o.QuestionOptionId === edit.radioOptionId).map((o) => ({ ...o, NumericValue: null }));
        }
        if (type.value === QuestionType.Multiple) {
            return options.filter((o) => edit.optionValues[o.QuestionOptionId]).map((o) => ({ ...o, NumericValue: null }));
        }
        if (type.value === QuestionType.MultipleNumeric) {
            return options.map((o) => {
                const raw = edit.optionValues[o.QuestionOptionId];
                const value = raw === null || raw === '' || typeof raw === 'boolean' ? null : Number(raw);
                return { ...o, Value: value === null ? null : String(value), NumericValue: value, NumericSum: value };
            });
        }
        return options.filter((o) => edit.optionValues[o.QuestionOptionId]);
    }

    async function save() {
        editing.value = false;
        saving.value = true;

        const freetext = isEventParticipant.value ? JSON.stringify(edit.participants) : edit.freetext;
        const questionOptions = selectedOptions();

        try {
            if (props.answer) {
                await updateSurveyAnswer(props.eventParticipantId, {
                    ...props.answer,
                    Freetext: freetext,
                    Question: { ...props.answer.Question, QuestionOptions: questionOptions },
                });
            } else {
                await createSurveyAnswer(props.surveyResponseId, props.eventParticipantId, {
                    Freetext: freetext,
                    Question: { QuestionId: props.question.QuestionId, QuestionOptions: questionOptions },
                });
            }
            emit('saved');
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeading });
        } finally {
            saving.value = false;
        }
    }
</script>

<template>
    <article class="question-card">
        <header>
            <QuestionMetaBadges :question="surveyQuestion" :answered="answered" />
            <h3 class="h4 m-0-t">{{ label }} {{ question.Title }}</h3>
        </header>

        <div v-if="question.Text" class="m-sm-b" v-html="question.Text"></div>

        <!-- View -->
        <template v-if="!editing">
            <!-- Accompanying guest: own entry in the accompanying-guests answer -->
            <template v-if="isEventParticipant">
                <div v-for="entry in ownParticipantEntries" :key="String(entry.Sqiid)">
                    <div class="text-answer">
                        <div v-for="(field, fieldIndex) in entry.Fields ?? []" :key="fieldIndex">
                            <span class="fw-600">{{ field.Title }}:</span>
                            {{ field.Value }}
                        </div>
                    </div>
                </div>
            </template>

            <template v-else>
                <QuestionAnswer
                    :question="surveyQuestion"
                    :free-text="answer?.Freetext ?? null"
                    :answer-options="answerOptions"
                    :resources="resources"
                    :response-id="surveyResponseId"
                    :resource-preview-urls="resourcePreviewUrls" />

                <template v-if="answer && question.DefaultValue">
                    <p v-if="contactValueDiff" class="small">{{ $Resources.EventResponseCurrentValueLabel }}: {{ contactValueDiff }}</p>

                    <BsAlert :type="AlertTypes.Warning" :title="$Resources.SurveyEditResponseAlertHeading" :dismissible="false" class="m-0-b">
                        <template #content>
                            {{ $Resources.EventResponseHasDefaultValuePart1 }}
                            <a href="javascript:void(0)" @click="emit('showContact')">{{ $Resources.EventResponseHasDefaultValuePart2 }}</a>
                            {{ $Resources.EventResponseHasDefaultValuePart3 }}
                        </template>
                    </BsAlert>
                </template>
            </template>
        </template>

        <!-- Edit -->
        <template v-if="editing">
            <template v-if="isEventParticipant">
                <template v-for="entry in edit.participants.Participants ?? []" :key="String(entry.Sqiid)">
                    <div v-if="sameSqiid(entry.Sqiid)">
                        <h4 class="m-0-t">{{ entry.Sqiid }}/{{ eventParticipantAnswer.Count }}</h4>
                        <FormTextArea
                            v-for="(field, fieldIndex) in entry.Fields ?? []"
                            :key="fieldIndex"
                            class="form-group m-sm-b"
                            :label="field.Title"
                            :rows="3"
                            :modelValue="field.Value"
                            @update:modelValue="(value: string) => (field.Value = value)" />
                    </div>
                </template>
            </template>

            <FormTextArea
                v-else-if="type === QuestionType.FreeText || type === QuestionType.Hidden"
                class="form-group m-sm-b"
                :label="question.Title ?? ''"
                srOnly
                :rows="3"
                :modelValue="edit.freetext"
                @update:modelValue="(value: string) => (edit.freetext = value)" />

            <FormInput
                v-else-if="type === QuestionType.InputField"
                class="form-group m-sm-b"
                :label="question.Title ?? ''"
                srOnly
                :modelValue="edit.freetext"
                @update:modelValue="(value: string) => (edit.freetext = value)" />

            <template v-if="question.QuestionOptions.length > 0">
                <FormRadioGroup
                    v-if="type === QuestionType.Single"
                    class="form-group m-sm-b"
                    :legend="null"
                    :options="optionItems"
                    v-model="edit.radioOptionId" />

                <FormCheckboxGroup
                    v-else-if="type === QuestionType.Multiple"
                    class="form-group m-sm-b"
                    :legend="null"
                    :options="optionItems"
                    :isSelected="(value) => edit.optionValues[value as string]"
                    @toggle="(value) => (edit.optionValues[value as string] = !edit.optionValues[value as string])" />

                <template v-else-if="type === QuestionType.MultipleNumeric">
                    <FormInput
                        v-for="option in question.QuestionOptions"
                        :key="option.QuestionOptionId"
                        class="form-group m-sm-b"
                        type="number"
                        :label="option.Value ?? ''"
                        :modelValue="edit.optionValues[option.QuestionOptionId]"
                        @update:modelValue="(value: string) => (edit.optionValues[option.QuestionOptionId] = value)" />
                </template>
            </template>
        </template>

        <footer class="m-xs-t m-sm-b">
            <ul v-if="showHistory && latestHistory" class="d-flex flex-wrap col-gap-md row-gap-xs list-unstyled">
                <li>
                    <i class="fa fa-warning c-warning m-xs-r" aria-hidden="true"></i>
                    <strong>{{ $Resources.SurveyResponseIsEditedText }}</strong>
                </li>
                <li>
                    <span class="glyphicon glyphicon-time m-xs-r"></span>
                    <time :datetime="latestHistory.ModifiedAt">{{ formatDateTime(latestHistory.ModifiedAt) }}</time>
                </li>
                <li>
                    <span class="glyphicon glyphicon-user"></span>
                    {{ latestHistory.ModifiedByUserName }}
                </li>
                <li>{{ $Resources.SurveyResponsePreviousResponseText }}: {{ previousAnswer }}</li>
            </ul>

            <div class="d-flex flex-wrap gap-xs">
                <template v-if="editing">
                    <button type="button" class="btn btn-default btn-sm" @click="editing = false">
                        {{ $Resources.SurveyResponseCancelChangeSurveyAnswer }}
                    </button>
                    <button type="button" class="btn btn-primary btn-sm" :disabled="saving" @click="save">
                        {{ $Resources.SurveyResponseSaveSurveyAnswer }}
                    </button>
                </template>

                <button v-else-if="!answer && !isEventParticipant && editableType" type="button" class="btn btn-sm btn-default" @click="startEdit">
                    <span class="glyphicon glyphicon-edit" aria-hidden="true"></span>
                    {{ $Resources.SurveyResponseAddSurveyAnswer }}
                </button>

                <button v-else-if="answer && (allowEditAnswer || isEventParticipant)" type="button" class="btn btn-sm btn-default" @click="startEdit">
                    <span class="glyphicon glyphicon-edit" aria-hidden="true"></span>
                    {{ $Resources.SurveyResponseChangeSurveyAnswer }}
                </button>
            </div>
        </footer>
    </article>
</template>
